import _ from 'lodash';
import React from 'react';
import AccountLink from '../components/common/AccountLink/AccountLink';
import AppLink from '../components/common/AppLink/AppLink';
import DrawerLink from '../components/common/drawers/ModuleDrawer/DrawerLink';
import { archiveFilter, tagFilter } from '../components/common/filters/consts';
import { IFilter } from '../components/common/filters/interfaces';
import RolesPopover from '../components/common/popover/RolesPopover';
import TaskDuePill from '../components/tasks/TaskDuePill';
import TaskStatusPill from '../components/tasks/TaskStatusPill';
import { ROUTE_PATHS } from '../routes/Routes';
import { TaskAvailableColumns } from '../services/react-query/tasks';
import { Preference, PREFERENCES_VALID_KEYS } from '../types/Preference';
import { Task, TaskStatus, TaskStatusFilter } from '../types/Task';
import { TaskTemplate } from '../types/TaskTemplate';
import { UserConfigurableModule } from '../types/User';
import { AvailableColumn } from '../uikit/itable/ITable';
import { humanizeDate } from './dateUtils';
import { mapOptionsFromPreferences } from './filterUtils';

export const TaskColumns: AvailableColumn<Task, TaskAvailableColumns>[] = [
  {
    key: 'name',
    label: 'Name',
    render: (task) => (
      <DrawerLink module={UserConfigurableModule.TASKS} uuid={task.uuid}>
        {task.name}
      </DrawerLink>
    ),
  },
  {
    key: 'task_template_name',
    label: 'Template',
    render: (task) => <AppLink to={`/${ROUTE_PATHS.TASK_TEMPLATES}/${task.task_template_uuid}`}>{task.task_template_name}</AppLink>,
  },
  {
    key: 'event_display_name',
    label: 'Event',
    render: (task) => <AppLink to={`/${ROUTE_PATHS.EVENTS}/${task.event_uuid}`}>{task.event_display_name}</AppLink>,
  },
  {
    key: 'account_name',
    label: 'Account Name',
    render: (task) => <AccountLink uuid={task.account_uuid} displayText={task.account_name} />,
  },
  {
    key: 'account_code',
    label: 'Account Code',
    render: (task) => <AccountLink uuid={task.account_uuid} displayText={task.account_code} />,
  },
  {
    key: 'employee_display_name',
    label: 'Assigned To',
    render: (task) => <AppLink to={`/${ROUTE_PATHS.EMPLOYEES}/${task.employee_uuid}`}>{task.employee_display_name}</AppLink>,
  },
  {
    key: 'employee_email',
    label: 'Assigned To Email',
    render: (task) => task.employee_email,
  },
  {
    key: 'start_date',
    label: 'Start Date',
    render: (task) => humanizeDate(task.start_date),
  },
  {
    key: 'due_date',
    label: 'Due Date',
    render: (task) => humanizeDate(task.due_date),
  },
  {
    key: 'status',
    label: 'Status',
    render: (task) => <TaskStatusPill status={task.status} />,
  },
  {
    key: 'due_status',
    label: 'Due Status',
    sortable: false,
    render: (task) => <TaskDuePill task={task} />,
  },
  {
    key: 'tags',
    label: 'Tags',
    sortable: false,
    render: (task) => <RolesPopover roles={_.map(task.tags, 'preference_value')} />,
  },
];

export const TaskFilters = (preferences?: Preference[], taskTemplates?: TaskTemplate[]): IFilter[] => [
  {
    label: 'Event Type',
    key: PREFERENCES_VALID_KEYS.EVENT_TYPE,
    type: 'dropdown',
    options: mapOptionsFromPreferences(preferences || [], PREFERENCES_VALID_KEYS.EVENT_TYPE),
  },
  {
    label: 'Event Location',
    key: PREFERENCES_VALID_KEYS.EVENT_LOCATION,
    type: 'dropdown',
    options: mapOptionsFromPreferences(preferences || [], PREFERENCES_VALID_KEYS.EVENT_LOCATION),
  },
  {
    label: 'Task Status',
    key: 'task_status',
    type: 'dropdown',
    options: [
      { label: 'Active', value: TaskStatusFilter.ACTIVE },
      { label: 'Ready', value: TaskStatusFilter.READY },
      { label: 'In Progress', value: TaskStatusFilter.IN_PROGRESS },
      { label: 'On Hold', value: TaskStatusFilter.ON_HOLD },
      { label: 'Overdue', value: TaskStatusFilter.OVERDUE },
      { label: 'Completed', value: TaskStatusFilter.COMPLETED },
      { label: 'Not Started', value: TaskStatusFilter.NOT_STARTED },
    ],
  },
  {
    label: 'Assigned To',
    key: 'employee',
    type: 'employee',
  },
  {
    label: 'Task Type',
    key: 'task_type',
    type: 'dropdown',
    options: mapOptionsFromPreferences(preferences || [], PREFERENCES_VALID_KEYS.TASK_TYPE),
  },
  {
    label: 'Task Template',
    key: 'task_template',
    type: 'dropdown',
    options: _.map(taskTemplates, (template: { name: string; uuid: string }) => ({
      label: template.name,
      value: template.uuid,
    })),
  },
  {
    label: 'Account',
    key: 'account',
    type: 'account',
  },
  {
    label: 'Account Manager',
    key: 'account_manager',
    type: 'employee',
  },
  tagFilter,
  archiveFilter,
];

const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.NOT_STARTED]: '#c0c0c0',
  [TaskStatus.READY]: '#42A5F5',
  [TaskStatus.IN_PROGRESS]: '#AD49E1',
  [TaskStatus.ON_HOLD]: '#FFB74D',
  [TaskStatus.COMPLETED]: '#35B23D',
};

export function taskBaseColor(status: TaskStatus) {
  return TASK_STATUS_COLORS[status];
}
